import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import logo from "@/assets/logo.webp";

const prefetchRoute = (path: string) => {
  const routeMap: Record<string, () => Promise<unknown>> = {
    "/about": () => import("@/pages/About"),
    "/activities": () => import("@/pages/Activities"),
    "/benefits": () => import("@/pages/Benefits"),
    "/insights": () => import("@/pages/Insights"),
    "/team": () => import("@/pages/Team"),
    "/contact": () => import("@/pages/Contact"),
    "/apply": () => import("@/pages/Apply"),
    "/portfolio": () => import("@/pages/PortfolioComposition"),
  };
  if (routeMap[path]) routeMap[path]();
};

const links = [
  { label: "About", href: "/about" },
  { label: "Activities", href: "/activities" },
  { label: "Benefits", href: "/benefits" },
  { label: "Insights", href: "/insights" },
  { label: "Portfolio", href: "/portfolio" },
  { label: "Team", href: "/team" },
  { label: "Contact", href: "/contact" },
];

const Header = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header className="fixed top-0 left-0 right-0 z-[200] bg-navy-dark/95 backdrop-blur-md border-b border-white/10 pt-safe px-safe">
      <div className="px-4 sm:px-6 md:px-12 py-3 sm:py-4 lg:py-5 short:py-2 flex justify-between items-center gap-3 text-paper">
        {/* Brand */}
        <Link to="/" className="flex items-center gap-3 group" aria-label="JSA Investment Club">

          <img
            src={logo}
            alt="JSA Investment Club logo"
            className="h-9 sm:h-10 lg:h-11 w-auto [filter:brightness(0)_invert(1)] opacity-90 group-hover:opacity-70 transition-opacity"
            width={44}
            height={44}
          />
          <span className="hidden md:block h-6 w-px bg-white/20" />
          <span className="hidden md:block text-[9px] uppercase tracking-[0.3em] font-light leading-tight text-paper/70">
            JSA Investment
            <br />
            Club
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-5 lg:gap-8 xl:gap-10 text-[11px] uppercase tracking-[0.2em] font-semibold">
          {links.map((l) => {
            const active = location.pathname === l.href;
            return (
              <Link
                key={l.label}
                to={l.href}
                onMouseEnter={() => prefetchRoute(l.href)}
                className={`relative py-1 transition-colors after:absolute after:left-0 after:right-0 after:-bottom-0.5 after:h-px after:transition-colors ${
                  active
                    ? "text-paper after:bg-steel"
                    : "text-paper/70 hover:text-steel-light after:bg-transparent"
                }`}
              >
                {l.label}
              </Link>
            );
          })}
          <Link
            to="/apply"
            onMouseEnter={() => prefetchRoute("/apply")}
            className="ml-2 inline-flex items-center bg-paper text-navy-dark px-5 py-2 text-[11px] uppercase tracking-[0.2em] font-bold hover:bg-steel hover:text-paper transition-colors"
          >
            Apply
          </Link>
        </nav>

        {/* Mobile toggle */}
        <button
          onClick={() => setOpen(!open)}
          className="lg:hidden inline-flex items-center justify-center w-11 h-11 -mr-2 text-paper"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <nav className="lg:hidden bg-navy-dark border-t border-white/10 px-6 py-6 pb-safe flex flex-col gap-4 text-xs uppercase tracking-[0.2em] font-semibold overflow-y-auto overscroll-contain max-h-[calc(100dvh-var(--header-h))] short:grid short:grid-cols-2 short:gap-x-6">
          {links.map((l) => (
            <Link
              key={l.label}
              to={l.href}
              className={`flex items-center py-1 ${
                location.pathname === l.href
                  ? "text-paper"
                  : "text-paper/70 hover:text-steel-light"
              }`}
            >
              {l.label}
            </Link>
          ))}
          <Link
            to="/apply"
            className="flex items-center justify-center bg-paper text-navy-dark px-5 py-3 text-center font-bold mt-2 short:col-span-2 short:mt-0"
          >
            Apply
          </Link>
        </nav>
      )}
    </header>
  );
};

export default Header;
